import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  UploadCloud, DownloadCloud, Loader2, CheckCircle2, Info, RefreshCw, FileJson, Rocket, AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { apiContentStatus, apiContentPreparePublish, apiContentExport, apiContentImport, errMsg } from "../adminApi";

const COLL_LABELS = {
  site_config: "Configurazione sito",
  settings: "Impostazioni",
  categories: "Categorie",
  brands: "Marchi",
  products: "Prodotti",
  reviews: "Recensioni",
  price_history: "Storico prezzi",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export default function Publish() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preparing, setPreparing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const loadStatus = useCallback(async () => {
    try {
      const s = await apiContentStatus();
      setStatus(s);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const handlePrepare = async () => {
    setPreparing(true);
    try {
      const res = await apiContentPreparePublish();
      const total = Object.values(res.counts || {}).reduce((n, v) => n + v, 0);
      toast.success(`Contenuti preparati (${total} elementi). Ora premi Pubblica/Deploy per allineare il sito online.`);
      await loadStatus();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setPreparing(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const payload = await apiContentExport();
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `rinova-configurazione-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      const total = Object.values(payload.collections || {}).reduce((n, arr) => n + arr.length, 0);
      toast.success(`Configurazione esportata (${total} elementi).`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setExporting(false);
    }
  };

  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      let data;
      try {
        data = JSON.parse(reader.result);
      } catch {
        toast.error("Il file selezionato non è un JSON valido.");
        return;
      }
      if (!data || !data.collections) {
        toast.error("Il file non è una configurazione RINOVA valida.");
        return;
      }
      setImporting(true);
      try {
        const res = await apiContentImport(data);
        const total = Object.values(res.counts || {}).reduce((n, v) => n + v, 0);
        toast.success(`Sito aggiornato: ${total} elementi applicati.`);
        await loadStatus();
      } catch (e) {
        toast.error(errMsg(e));
      } finally {
        setImporting(false);
      }
    };
    reader.readAsText(file);
  };

  const counts = status?.counts || {};

  return (
    <div className="mx-auto max-w-4xl" data-testid="publish-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-chalk">Pubblicazione</h1>
          <p className="mt-1 max-w-2xl text-sm text-steel">
            Aggiorna i contenuti del <b>sito online</b> con la configurazione dell'anteprima, senza dover ripubblicare.
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={loadStatus} className="text-steel hover:text-chalk" data-testid="publish-refresh">
          <RefreshCw className="h-4 w-4" /> Aggiorna stato
        </Button>
      </div>

      {/* Azione principale: prepara per la pubblicazione (1 clic) */}
      <div className="mt-5 rounded-2xl border border-lime/25 bg-lime/5 p-5" data-testid="publish-prepare-card">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-lime text-ink">
              <Rocket className="h-5 w-5" />
            </div>
            <div>
              <h2 className="font-display text-lg font-bold text-chalk">Prepara per la pubblicazione</h2>
              <p className="mt-1 max-w-xl text-sm text-steel">
                Dopo aver aggiunto o modificato prodotti/contenuti, premi questo pulsante: fotografa lo stato attuale dell'anteprima. Poi premi <b>Pubblica/Deploy</b> e il sito online si allineerà automaticamente (foto e nuovi prodotti inclusi).
              </p>
            </div>
          </div>
          <Button onClick={handlePrepare} disabled={preparing} variant="lime" className="shrink-0 justify-center" data-testid="publish-prepare-btn">
            {preparing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
            Prepara per la pubblicazione
          </Button>
        </div>
        {status && !loading && (
          status.in_sync ? (
            <div className="mt-4 flex items-center gap-2 rounded-lg bg-lime/10 px-3 py-2 text-sm text-lime" data-testid="publish-sync-ok">
              <CheckCircle2 className="h-4 w-4" /> Contenuti pronti: l'anteprima e il pacchetto di pubblicazione coincidono.
            </div>
          ) : (
            <div className="mt-4 flex items-center gap-2 rounded-lg bg-amber-400/10 px-3 py-2 text-sm text-amber-300" data-testid="publish-sync-stale">
              <AlertTriangle className="h-4 w-4" /> Hai modifiche non ancora preparate: premi "Prepara per la pubblicazione" prima di pubblicare.
            </div>
          )
        )}
      </div>

      {/* Come funziona (dettaglio) */}
      <div className="mt-5 flex gap-3 rounded-xl border border-line bg-surface2 p-4" data-testid="publish-howto">
        <Info className="mt-0.5 h-5 w-5 shrink-0 text-steel" />
        <div className="text-sm text-chalk">
          <p className="font-semibold">Due modi per aggiornare il sito online</p>
          <ol className="mt-2 list-decimal space-y-1 pl-5 text-steel">
            <li><b>Con pubblicazione</b> (consigliato): premi “Prepara per la pubblicazione”, poi il tasto <b>Pubblica/Deploy</b> della piattaforma. Il sito online si allinea da solo.</li>
            <li><b>Senza ripubblicare</b>: premi “Esporta configurazione”, poi nell'admin del <b>sito online</b> usa “Importa configurazione” caricando il file.</li>
          </ol>
        </div>
      </div>

      {/* Azioni */}
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        {/* Esporta */}
        <div className="flex flex-col rounded-2xl border border-line bg-surface2 p-5" data-testid="publish-export-card">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-surface3 text-lime">
            <DownloadCloud className="h-5 w-5" />
          </div>
          <h2 className="mt-3 font-display text-lg font-bold text-chalk">Esporta configurazione</h2>
          <p className="mt-1 flex-1 text-sm text-steel">
            Scarica un file con l'attuale configurazione (hero, categorie, marchi, sezioni, catalogo e recensioni). Le immagini restano su Cloudinary.
          </p>
          <Button onClick={handleExport} disabled={exporting} variant="secondary" className="mt-4 justify-center" data-testid="publish-export-btn">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <DownloadCloud className="h-4 w-4" />}
            Esporta configurazione
          </Button>
        </div>

        {/* Importa */}
        <div className="flex flex-col rounded-2xl border border-line bg-surface2 p-5" data-testid="publish-import-card">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-surface3 text-lime">
            <UploadCloud className="h-5 w-5" />
          </div>
          <h2 className="mt-3 font-display text-lg font-bold text-chalk">Importa configurazione</h2>
          <p className="mt-1 flex-1 text-sm text-steel">
            Applica al sito <b>corrente</b> un file di configurazione esportato. Aggiornamento immediato e non distruttivo verso ordini e recensioni dei clienti.
          </p>
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; handleFile(f); }}
            data-testid="publish-import-input"
          />
          <Button onClick={() => fileRef.current?.click()} disabled={importing} variant="lime" className="mt-4 justify-center" data-testid="publish-import-btn">
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
            Importa configurazione
          </Button>
        </div>
      </div>

      {/* Stato attuale */}
      <div className="mt-5 rounded-2xl border border-line bg-surface2 p-5" data-testid="publish-status-card">
        <div className="flex items-center gap-2">
          <FileJson className="h-4 w-4 text-steel" />
          <h2 className="font-display text-base font-bold text-chalk">Stato di questo sito</h2>
        </div>
        {loading ? (
          <div className="mt-3 flex items-center gap-2 text-sm text-steel"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento…</div>
        ) : (
          <>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
              {status?.applied_version ? (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-lime/10 px-3 py-1 font-medium text-lime" data-testid="publish-status-applied">
                  <CheckCircle2 className="h-4 w-4" /> Configurazione applicata
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-surface3 px-3 py-1 font-medium text-steel">
                  Nessuna configurazione importata (contenuti di default)
                </span>
              )}
              <span className="text-tertiary">Ultimo aggiornamento: {fmtDate(status?.applied_at)}</span>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
              {Object.entries(COLL_LABELS).map(([key, label]) => (
                <div key={key} className="rounded-xl border border-line bg-surface p-3" data-testid={`publish-count-${key}`}>
                  <p className="text-xl font-extrabold text-chalk">{counts[key] ?? 0}</p>
                  <p className="text-xs text-steel">{label}</p>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
